import { Button } from "@/components/ui/button";
import { storage } from "@/lib/firebase";
import { getDownloadURL, ref } from "firebase/storage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch privacy policy content from Firebase Storage
  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch markdown file from Firebase Storage
        const storagePath = "privacyPolicy/current.md";
        const fileRef = ref(storage, storagePath);
        const downloadURL = await getDownloadURL(fileRef);

        // Fetch the markdown content
        const response = await fetch(downloadURL);
        if (!response.ok) {
          throw new Error("Failed to fetch Privacy Policy content");
        }

        const markdown = await response.text();
        setContent(markdown);
      } catch (err: any) {
        console.error("Error fetching Privacy Policy:", err);
        if (err.code === "storage/object-not-found") {
          setError("Privacy Policy document not found. Please contact support.");
        } else {
          setError("Failed to load Privacy Policy. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Privacy Policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow text-center">
          <h1 className="text-2xl font-semibold text-red-600">Error</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
          <div className="pt-4">
            <Button variant="outline" onClick={() => navigate("/")}>
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b">
          <h1 className="text-3xl font-semibold text-center">Glean Ag Privacy Policy</h1>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <Button
            onClick={() => navigate("/")}
            className="w-full"
          >
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
}
